import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { MobilePageTitle } from "../components/MobilePageTitle";
import { MODES, VIDEO_INFLUENCER_ENABLED } from "./v2/modesCatalog";
import { setupAutoplayVideo } from "./v2/components/HomeCards";
import { getUserPlan } from "../lib/planUtils";
import { getModePlanLock, type ModePlanLock } from "../lib/planCapabilities";
import { FeatureLockedModal } from "../components/FeatureLockedModal";
import { useUserGroup } from "../hooks/useUserGroup";

/**
 * Pantalla "Modos" — lista los modos de generación de Interfaz V2 (los mismos
 * del design-row de la home), uno abajo del otro, cada uno con su video,
 * título y descripción. Al tocar uno se entra a ese modo en V2.
 */
export const Modos = () => {
  const navigate = useNavigate();
  // Modos `devOnly` (ej. "Video con influencer") solo se listan para dev testers.
  const { isDevTester } = useUserGroup();

  // Plan del usuario para gatear el acceso a los modos. Arrancamos del cache
  // de localStorage (mismo que llena el Sidebar) para tener el plan al instante
  // y refrescamos con el valor real. Si todavía no se conoce, no bloqueamos.
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem("atlas-user-plan");
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelled = false;
    getUserPlan().then((p) => {
      if (!cancelled && p) setPlan(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  // Lock a mostrar cuando el plan no alcanza para el modo tocado.
  const [modeLock, setModeLock] = useState<ModePlanLock | null>(null);

  return (
    <div className="px-4 sm:px-8 pt-2 pb-[calc(env(safe-area-inset-bottom,0px)+6rem)] md:pb-8 max-w-content mx-auto min-h-screen">
      {/* Título mobile (desktop usa el header de abajo) */}
      <MobilePageTitle>Modos</MobilePageTitle>
      {/* Desktop header */}
      <div className="hidden sm:flex items-center justify-between mb-5 h-11">
        <h1 className="text-[17px] font-semibold text-brand-black/90 mt-4">
          Modos
        </h1>
      </div>

      <div className="max-w-2xl mx-auto space-y-7 sm:space-y-8">
        {MODES.filter((m) => {
          // "Video con influencer" oculto para todos mientras el flag esté off.
          if (m.path === "video-influencer" && !VIDEO_INFLUENCER_ENABLED) return false;
          // Modos devOnly solo para dev testers.
          return !m.devOnly || isDevTester === true;
        }).map((mode) => {
          // Navegamos con `presetData` en el state para que InterfazV2 abra el
          // sheet del modo en el primer paint (mecanismo `initialNavPreset`) y
          // NO se vea un flash de la home V2 al montar.
          const goToMode = () => {
            // Gating por plan: si el modo está bloqueado para el plan actual,
            // NO entramos — mostramos el modal de "función exclusiva".
            const lock = getModePlanLock(mode.preset.kind, plan);
            if (lock) {
              setModeLock(lock);
              return;
            }
            navigate(`/interfaz-v2/modo/${mode.path}`, {
              state: { presetData: mode.preset, collectionPresets: [] },
            });
          };
          return (
            <div
              key={mode.path}
              onClick={goToMode}
              className="w-full text-left group cursor-pointer flex flex-col"
            >
              {/* Carátula en video, 4 esquinas redondeadas (igual que la card
                  de modo en la home V2). */}
              <div className="relative aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
                <video
                  ref={setupAutoplayVideo}
                  src={mode.video}
                  poster={mode.poster}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  aria-label={mode.preset.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                />
                {/* Botón Generar sobre el video (abajo a la derecha). */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToMode();
                  }}
                  className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
                >
                  <Sparkles className="w-4 h-4" />
                  Generar
                </button>
              </div>
              {/* Texto debajo del video — sin chrome, igual que en la home. */}
              <div className="mt-3 flex flex-col gap-0.5">
                <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 sm:group-hover:text-brand-primary">
                  {mode.preset.title}
                </h3>
                <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
                  {mode.preset.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <FeatureLockedModal
        open={modeLock !== null}
        onClose={() => setModeLock(null)}
        title={modeLock?.title ?? ""}
        message={modeLock?.message ?? ""}
      />
    </div>
  );
};
